import React, { useState, useEffect } from "react";
import { Typography, IconButton, Collapse, Tooltip } from "@mhome/ui";
import { InfoOutlined, Error } from "@mui/icons-material";
import BoolInput from "../input/bool-input";
import TextInput from "../input/text-input";
import NumberInput from "../input/number-input";
import NumberUnitInput from "../input/number-unit-input";
import FixedSelectInput from "../input/fixed-select-input";
import TimeGeneralInput from "../input/time-general-input";
import DateGeneralInput from "../input/date-general-input";
import TimestampInput from "../input/timestamp-input";
import { prepareWidget } from "./widget-util";
import debounce from "lodash/debounce";
import { useWebSocket } from "../ws/websocket";
import { updateWidgetData } from "../service/widget-service";
import { useComponentBackgroundColor } from "./adaptive-theme-provider";
import { useEventEmitter } from "../ws/event-context";

const AutomationWidget = ({ widgets }) => {
  const [expandedWidgets, setExpandedWidgets] = useState({});
  const [localWidgets, setLocalWidgets] = useState(widgets);
  const { sendAndWaitWs } = useWebSocket();
  const getComponentBgColor = useComponentBackgroundColor();
  const { subscribe } = useEventEmitter();

  // Sync local widgets when props change and sort by name
  useEffect(() => {
    console.debug("widgets", widgets);
    if (widgets && Array.isArray(widgets)) {
      const sortedWidgets = [...widgets].sort((a, b) => {
        const nameA = (a.name || "").toLowerCase();
        const nameB = (b.name || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setLocalWidgets(sortedWidgets);
    } else {
      setLocalWidgets(widgets);
    }
  }, [widgets]);

  // Subscribe to widget updates
  useEffect(() => {
    if (!subscribe) return;

    const { unsubscribe } = subscribe((target, data) => {
      if (target === "/widget/reportUpdate") {
        const payload = JSON.parse(data.payload);
        const updatedWidgets = [];
        for (let change of payload.changes) {
          if (change.type === "UPSERT") {
            updatedWidgets.push(change.widget);
          }
        }
        if (updatedWidgets.length > 0) {
          handleWidgetUpdateEvent(updatedWidgets);
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [subscribe, localWidgets]);

  const handleWidgetUpdateEvent = (updatedWidgets) => {
    const widgetsArray = Array.isArray(updatedWidgets)
      ? updatedWidgets
      : [updatedWidgets];

    setLocalWidgets((prevWidgets) => {
      if (!prevWidgets || !Array.isArray(prevWidgets)) return prevWidgets;

      let hasUpdates = false;
      const newWidgets = prevWidgets.map((widget) => {
        const updatedWidget = widgetsArray.find((w) => w.id === widget.id);
        if (updatedWidget) {
          hasUpdates = true;
          return { ...widget, ...updatedWidget };
        }
        return widget;
      });

      if (hasUpdates) {
        // Re-sort after updates
        return newWidgets.sort((a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });
      }
      return prevWidgets;
    });
  };

  const toggleWidgetExpanded = (widgetId) => {
    setExpandedWidgets((prev) => ({
      ...prev,
      [widgetId]: !prev[widgetId],
    }));
  };

  const handleWidgetValueUpdate = async (widgetId, value) => {
    try {
      const widget = localWidgets.find((w) => w.id === widgetId);
      const items = [
        {
          ...widget,
          value: value,
        },
      ];

      const response = await updateWidgetData(sendAndWaitWs, items);
      if (response && !response.error) {
        // Update local state
        setLocalWidgets((prevWidgets) =>
          prevWidgets.map((w) =>
            w.id === widgetId ? { ...w, value: value } : w
          )
        );
      }
    } catch (error) {
      console.error("Error updating widget value:", error);
    }
  };

  // Debounced update function to avoid too many API calls
  const debouncedUpdate = debounce((widgetId, value) => {
    handleWidgetValueUpdate(widgetId, value);
  }, 300);

  const renderWidgetInput = (widget) => {
    // Prepare the widget for rendering
    const prepared = { ...widget };
    prepareWidget(prepared);
    const valueSpec = { ...prepared };

    const onChange = (value) => {
      // Update local state immediately for responsive UI
      const newValue = value.toString();
      setLocalWidgets((prevWidgets) =>
        prevWidgets.map((w) =>
          w.id === widget.id ? { ...w, value: newValue } : w
        )
      );

      // The actual API call is debounced
      debouncedUpdate(widget.id, newValue);
    };

    switch (widget.dataType?.toLowerCase()) {
      case "bool":
        return <BoolInput valueSpec={valueSpec} onChange={onChange} />;
      case "number":
        return (
          <NumberInput
            valueSpec={valueSpec}
            onChange={onChange}
            onError={() => {}}
          />
        );
      case "temperature":
      case "temperaturediff":
      case "weight":
      case "length":
      case "duration":
        return (
          <NumberUnitInput
            valueSpec={valueSpec}
            onChange={(value, unit) => onChange(value.toString() + unit)}
            onError={() => {}}
          />
        );
      case "timegeneral":
        return <TimeGeneralInput valueSpec={valueSpec} onChange={onChange} />;
      case "dategeneral":
        return <DateGeneralInput valueSpec={valueSpec} onChange={onChange} />;
      case "timestamp":
        return (
          <TimestampInput
            valueSpec={valueSpec}
            onChange={onChange}
            onError={() => {}}
          />
        );
      case "string":
        if (valueSpec.enums && valueSpec.enums.length > 0) {
          return (
            <FixedSelectInput
              valueSpec={valueSpec}
              onChange={onChange}
              onError={() => {}}
            />
          );
        } else {
          return (
            <div
              style={{
                width: "100%",
                height: "32px",
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <TextInput
                valueSpec={valueSpec}
                onChange={onChange}
                style="compact"
              />
            </div>
          );
        }
      default:
        console.error("Unknown widget data type:", JSON.stringify(widget));
        return <Error />;
    }
  };

  if (!localWidgets || localWidgets.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        marginTop: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        width: "100%",
        padding: "16px",
        borderRadius: "16px",
        border: "1px solid hsl(var(--border))",
        backgroundColor: getComponentBgColor("hsl(var(--card))"),
      }}
    >
      {localWidgets.map((widget, index) => (
        <div key={widget.id || index}>
          {/* Main row with label and input */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                flex: 1,
              }}
            >
              <Typography
                variant="body2"
                className="font-medium"
                color="textPrimary"
              >
                {widget.displayName || widget.name}
              </Typography>
              {widget.description && (
                <Tooltip
                  title={
                    expandedWidgets[widget.id] ? "Hide details" : "Show details"
                  }
                >
                  <IconButton
                    size="small"
                    onClick={() => toggleWidgetExpanded(widget.id)}
                    style={{
                      padding: "4px",
                      transform: expandedWidgets[widget.id]
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.2s ease-in-out",
                    }}
                  >
                    <InfoOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                flexShrink: 0,
                width: "30%",
                minWidth: "120px",
              }}
            >
              {renderWidgetInput(widget)}
            </div>
          </div>
          {/* Collapsible description */}
          {widget.description && (
            <Collapse in={expandedWidgets[widget.id]}>
              <div
                style={{
                  marginTop: "8px",
                  padding: "12px",
                  borderRadius: "8px",
                  backgroundColor: "transparent",
                  border: "1px solid hsl(var(--border))",
                }}
              >
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className="whitespace-pre-wrap"
                >
                  {widget.description}
                </Typography>
              </div>
            </Collapse>
          )}
        </div>
      ))}
    </div>
  );
};

export default AutomationWidget;
